import { useEffect, useState } from 'react'
import { Alert, Button, Spin, Typography, Upload, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import * as api from '../lib/api'
import AppraisalWorkflowCard from './AppraisalWorkflowCard'
import RatingDistributionChart from './RatingDistributionChart'

const { Text, Title } = Typography

function ImportHeader ({ report, uploading, canManage, onUpload }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      <div>
        <Title level={4} style={{ margin: 0 }}>Imported Performance Report</Title>
        {report && <Text type='secondary'>{report.source_filename} · Updated {new Date(report.updated_at).toLocaleString()}</Text>}
        <Text type='secondary' style={{ display: 'block' }}>Excel columns: Rating and Status</Text>
      </div>
      {canManage && (
        <Upload accept='.xlsx' showUploadList={false} beforeUpload={onUpload}>
          <Button type='primary' icon={<UploadOutlined />} loading={uploading}>
            {report ? 'Replace Excel Report' : 'Import Excel Report'}
          </Button>
        </Upload>
      )}
    </div>
  )
}

function ratingCode (rating) {
  const normalized = String(rating || '').trim().toLowerCase()
  if (normalized === 'ni' || normalized === 'needs improvement') return 'NI'
  if (normalized === 'meets expectations') return 'ME'
  if (['me-', 'me', 'me+'].includes(normalized)) return normalized.toUpperCase()
  if (normalized === 'exceeds expectations') return 'EE'
  if (['ee-', 'ee', 'ee+'].includes(normalized)) return normalized.toUpperCase()
  if (['e', 'ex', 'excel', 'excels'].includes(normalized)) return 'E'
  return null
}

function importedRatingData (metrics, ratingCategories) {
  const counts = metrics.rating_counts || {}
  const ratedTotal = Object.entries(counts).reduce(
    (total, [rating, count]) => ratingCode(rating) ? total + Number(count || 0) : total,
    0
  )
  const categories = (ratingCategories || []).map(category => {
    const actualCount = Object.entries(counts).reduce((total, [rating, count]) => (
      category.bucket_codes?.includes(ratingCode(rating))
        ? total + Number(count || 0)
        : total
    ), 0)
    return {
      ...category,
      actual: ratedTotal ? (actualCount / ratedTotal) * 100 : 0
    }
  })
  return {
    categories,
    headcount_total: metrics.total || 0,
    rated_total: ratedTotal
  }
}

function RatingStatusReport ({ data, ratingCategories }) {
  const metrics = data.metrics || {}
  const completed = Object.entries(metrics.status_counts || {}).reduce(
    (total, [status, count]) => status.trim().toLowerCase() === 'completed' ? total + Number(count || 0) : total,
    0
  )
  return (
    <>
      <AppraisalWorkflowCard
        total={metrics.total || 0}
        completed={completed}
        showSecondaryStat={false}
        workflowStages={[]}
      />
      <RatingDistributionChart data={importedRatingData(metrics, ratingCategories)} />
    </>
  )
}

export default function ReadOnlySessionReport ({ sessionId, canManage, mode = 'report', refreshKey = 0 }) {
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    setLoading(true)
    api.getSessionReadOnlyReport(sessionId)
      .then(setPayload)
      .catch(error => message.error(error.message || 'Unable to load imported report'))
      .finally(() => setLoading(false))
  }, [refreshKey, sessionId])

  const upload = async file => {
    setUploading(true)
    try {
      const next = await api.uploadSessionReadOnlyReport(sessionId, file)
      setPayload(next)
      message.success('Imported report updated')
    } catch (error) {
      message.error(error.message || 'Unable to import report')
    } finally {
      setUploading(false)
    }
    return false
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
  const report = payload?.report
  if (mode === 'import') {
    return (
      <div>
        <ImportHeader report={report} uploading={uploading} canManage={canManage} onUpload={upload} />
        {!report && <Alert type='info' showIcon message='No report imported' description='Import an Excel workbook with Rating and Status columns.' />}
        {report && !report.data?.metrics?.status_counts && <Alert type='warning' showIcon message='Replace the existing report' description='The report format has changed. Import an Excel workbook with Rating and Status columns.' />}
        {report?.data?.metrics?.status_counts && <Alert type='success' showIcon message='Imported report ready' description='The Appraisals tab displays the completion and rating summaries from this report.' />}
      </div>
    )
  }
  return (
    <div>
      {!report && <Alert type='info' showIcon message='No report imported' description={canManage ? 'Use the Imported Performance Report tab to import an Excel workbook.' : 'The regional report has not been imported yet.'} />}
      {report && !report.data?.metrics?.status_counts && <Alert type='warning' showIcon message='Report replacement required' description={canManage ? 'Use the Imported Performance Report tab to replace the legacy workbook.' : 'The imported report must be replaced by HR.'} />}
      {report?.data?.metrics?.status_counts && <RatingStatusReport data={report.data} ratingCategories={payload.rating_categories} />}
    </div>
  )
}